import React from 'react';
import type { DynamicField } from '../core/types/DynamicField.types';
import { DynamicFieldType } from '../core/types/DynamicField.types';
import { Input } from '../../Input/Input';
import { Select } from '../../Select/Select';
import { Switch } from '../../Switch/Switch';
import { Button } from '../../Button/Button';
import { LookupField } from '../../LookupField';
import { useOptionalDynamicEntitiesContext, useDynamicEntitiesFetcher } from '../core/DynamicEntitiesProvider';
import { XMarkIcon } from '@heroicons/react/24/outline';

// Custom for now as it's specific metadata.

interface FieldEditorProps {
    field: DynamicField;
    genericLists?: any[];
    onSave: (field: DynamicField) => void;
    isPlansAndCriteria?: boolean;
    isNew?: boolean;
    isReadonly?: boolean;
    onCancel: () => void;
}

const FIELD_TYPE_LABELS: Record<string, string> = {
    [DynamicFieldType.Text]: 'Texto Corto',
    [DynamicFieldType.TextArea]: 'Texto Largo (Área)',
    [DynamicFieldType.NumberInteger]: 'Número Entero',
    [DynamicFieldType.NumberDecimal]: 'Número Decimal',
    [DynamicFieldType.Currency]: 'Moneda',
    [DynamicFieldType.Date]: 'Fecha',
    [DynamicFieldType.Time]: 'Hora',
    [DynamicFieldType.DateTime]: 'Fecha y Hora',
    [DynamicFieldType.Checkbox]: 'Casilla de Verificación',
    [DynamicFieldType.Select]: 'Selección Simple',
    [DynamicFieldType.Radio]: 'Botones de Opción (Radio)',
    [DynamicFieldType.Multiselect]: 'Selección Múltiple',
    [DynamicFieldType.Lookup]: 'Búsqueda (Lookup)',
    [DynamicFieldType.Switch]: 'Interruptor (Switch)',
    [DynamicFieldType.GenericEntity]: 'Entidad Genérica'
};

export const FieldEditor: React.FC<FieldEditorProps> = ({ field, genericLists = [], onSave, onCancel, isPlansAndCriteria, isNew, isReadonly }) => {
    const ctx = useOptionalDynamicEntitiesContext();
    // baseUrl ya termina en /api; la API v1 cuelga del mismo prefijo (${baseUrl}/v1/...).
    // No extraemos el origin para soportar baseUrl relativo (modo MFE detrás del shell).
    const baseUrl = ctx?.baseUrl ?? '';
    const http = useDynamicEntitiesFetcher();

    const [data, setData] = React.useState<DynamicField>({ ...field });

    const handleChange = (key: keyof DynamicField, value: any) => {
        setData(prev => ({ ...prev, [key]: value }));
    };

    const handleLabelChange = (lang: string, value: string) => {
        setData(prev => ({
            ...prev,
            labels: { ...prev.labels, [lang]: value }
        }));
    };

    return (
        <div className="flex flex-col w-full h-full relative overflow-hidden">
            {/* Header */}
            <div className="flex justify-between items-center mb-6 shrink-0 border-b border-gray-100 pb-4">
                <p className="font-medium text-base leading-6 text-primary-custom-600 dark:text-primary-custom-500 m-0">
                    {isReadonly
                        ? 'Ver campo'
                        : isPlansAndCriteria
                            ? (isNew ? 'Crear Plan' : 'Editar Plan')
                            : (isNew ? 'Nuevo Campo' : 'Editar campo')
                    }
                </p>
                <Button
                    type="outline"
                    iconOnly
                    size="sm"
                    onClick={onCancel}
                    leftIcon={<XMarkIcon className="w-5 h-5" />}
                    ariaLabel="Cerrar"
                />
            </div>

            {/* Scrollable Content */}
            <div className="flex flex-col gap-5 overflow-y-auto flex-1 pr-2 pb-2">
                {isPlansAndCriteria && (
                    <Input
                        label="Código"
                        value={data.code}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('code', e.target.value)}
                        disabled={isReadonly || (!isNew && !isPlansAndCriteria)}
                        required
                        inputSize="sm"
                        {...({ fullWidth: true } as any)}
                    />
                )}

                <div className="flex flex-col gap-5 w-full">
                    <Input
                        label="Etiqueta (ES)"
                        value={data.labels['es'] || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleLabelChange('es', e.target.value)}
                        disabled={isReadonly}
                        required
                        inputSize="sm"
                    />
                    <Input
                        label="Etiqueta (EN)"
                        value={data.labels['en'] || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleLabelChange('en', e.target.value)}
                        disabled={isReadonly}
                        inputSize="sm"
                    />
                </div>

                {!isPlansAndCriteria && (
                    <Select
                        label="Tipo"
                        options={Object.values(DynamicFieldType)
                            .filter(t => ![DynamicFieldType.Select, DynamicFieldType.Multiselect, DynamicFieldType.Lookup].includes(t as any))
                            .map(t => ({ label: FIELD_TYPE_LABELS[t as string] || t, value: t as string }))}
                        value={data.type as string}
                        onChange={val => handleChange('type', val as DynamicFieldType)}
                        disabled={isReadonly}
                        required
                        selectSize="sm"
                    />
                )}

                {!isPlansAndCriteria && (
                    <Select
                        label="Espacio horizontal"
                        options={[
                            { label: '50%', value: '1' },
                            { label: '100%', value: '2' }
                        ]}
                        value={String(data.layouts?.[0]?.colSpan || 1)}
                        onChange={val => {
                            const parsedVal = typeof val === "string" ? parseInt(val) : (val as number) || 1;
                            setData(prev => ({
                                ...prev,
                                layouts: [{
                                    ...(prev.layouts?.[0] || { breakpoint: 'md', rowSpan: 1, colStart: 0, rowStart: 0 }),
                                    colSpan: parsedVal
                                }]
                            }));
                        }}
                        disabled={isReadonly}
                        required
                        selectSize="sm"
                    />
                )}

                <div className="flex flex-col gap-3 w-full mt-2">
                    <Switch
                        label="Requerido"
                        labelPosition="end"
                        checked={data.required}
                        onCheckedChange={(checked: boolean) => handleChange('required', checked)}
                        disabled={isReadonly}
                        size="sm"
                    />
                    {!isPlansAndCriteria && (
                        <>
                            <Switch
                                label="Solo lectura"
                                labelPosition="end"
                                checked={data.readonly}
                                onCheckedChange={(checked: boolean) => handleChange('readonly', checked)}
                                disabled={isReadonly}
                                size="sm"
                            />
                            <Switch
                                label="Visible"
                                labelPosition="end"
                                checked={data.visible}
                                onCheckedChange={(checked: boolean) => handleChange('visible', checked)}
                                disabled={isReadonly}
                                size="sm"
                            />
                            <Switch
                                label="Único"
                                labelPosition="end"
                                checked={data.isUnique || false}
                                onCheckedChange={(checked: boolean) => handleChange('isUnique', checked)}
                                disabled={isReadonly}
                                size="sm"
                            />
                        </>
                    )}
                </div>

                {/* Type specific settings */}
                {(data.type === DynamicFieldType.NumberInteger ||
                    data.type === DynamicFieldType.NumberDecimal ||
                    data.type === DynamicFieldType.Currency) && (
                        <div className="flex flex-col gap-5">
                            <Input type="number" inputSize="sm" label="Mínimo" value={data.minValue === undefined || data.minValue === null ? '' : String(data.minValue)} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('minValue', parseFloat(e.target.value))} disabled={isReadonly} />
                            <Input type="number" inputSize="sm" label="Máximo" value={data.maxValue === undefined || data.maxValue === null ? '' : String(data.maxValue)} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('maxValue', parseFloat(e.target.value))} disabled={isReadonly} />
                        </div>
                    )}

                {(data.type === DynamicFieldType.Select ||
                    data.type === DynamicFieldType.Radio ||
                    data.type === DynamicFieldType.Multiselect) && (
                        <div className="flex flex-col gap-2">
                            <Select
                                label="Catálogo de Opciones"
                                options={genericLists.map((l: any) => ({ label: l.description || l.code, value: l.id }))}
                                value={data.genericListId}
                                onChange={val => handleChange('genericListId', val)}
                                placeholder={genericLists.length === 0 ? "No hay catálogos creados" : "Seleccionar catálogo..."}
                                disabled={isReadonly || genericLists.length === 0}
                                selectSize="sm"
                            />
                            {!isReadonly && genericLists.length === 0 && (
                                <p className="text-xs text-orange-600 bg-orange-50 p-2 rounded border border-orange-200 m-0">
                                    ⚠️ Debes crear un catálogo en "Configuración &gt; Listas Generales" antes de usar este tipo de campo.
                                </p>
                            )}
                        </div>
                    )}

                {data.type === DynamicFieldType.GenericEntity && (
                    <div className="flex flex-col gap-2">
                        <LookupField
                            label="Entidad Genérica"
                            entity="generic-entities"
                            displayFields={['Code', 'Description']}
                            value={data.genericEntityId || null}
                            onChange={(record) => handleChange('genericEntityId', record?.Id || null)}
                            fetcher={async (entity, request) => {
                                // 1. Map `Id` to `ID` in request to satisfy backend validation
                                const mappedRequest = {
                                    ...request,
                                    fields: request.fields.filter(f => f !== 'Id').concat('ID'),
                                    searchFields: request.searchFields?.map(f => f === 'Id' ? 'ID' : f),
                                    filters: request.filters?.map(group => group.map(cond => {
                                        const mappedCond: any = {};
                                        for (const key in cond) {
                                            mappedCond[key === 'Id' ? 'ID' : key] = cond[key as keyof typeof cond];
                                        }
                                        return mappedCond;
                                    }))
                                };

                                const res = await http(`${baseUrl}/v1/${entity}/search`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify(mappedRequest)
                                });

                                if (!res.ok) throw new Error('Error fetching generic entities');
                                const responseData = await res.json();

                                // 2. Map `ID` back to `Id` for LookupField compatibility
                                return {
                                    ...responseData,
                                    data: responseData.data.map((item: any) => ({
                                        ...item,
                                        Id: item.ID
                                    }))
                                };
                            }}
                        />
                        {!data.genericEntityId && (
                            <p className="text-xs text-orange-600 bg-orange-50 p-2 rounded border border-orange-200 m-0">
                                ⚠️ Debes elegir la entidad genérica relacionada a este campo.
                            </p>
                        )}
                    </div>
                )}
            </div>

            {/* Footer */}
            {!isReadonly && (
                <div className="mt-5 shrink-0 w-full pt-1">
                    <Button
                        type="outline"
                        size="sm"
                        onClick={() => onSave(data)}
                        className="w-full flex justify-center"
                    >
                        Guardar
                    </Button>
                </div>
            )}
        </div>
    );
};
